import React, { useMemo } from 'react';
import { Box, Tooltip, Typography, type TooltipProps } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { error, neutral, primary } from '_lex/_foundations/phxColors';

export type PhxBadgeCounterColor = 'default' | 'primary' | 'gray' | 'red';
export type PhxBadgeCounterSize = 'sm' | 'md';
export type PhxBadgeCounterBorder = 'circle' | 'rounded';

export interface PhxBadgeCounterProps {
  /**
   * The number to display in the badge
   */
  number: number;
  /**
   * When set (e.g. Policy TOC chapter pending count), wraps the badge in a MUI Tooltip and improves a11y.
   */
  tooltipTitle?: React.ReactNode;
  /**
   * Optional props forwarded to MUI Tooltip (excluding title and children).
   */
  tooltipProps?: Omit<TooltipProps, 'title' | 'children'>;
  /**
   * The color variant of the badge
   * @default 'default'
   */
  color?: PhxBadgeCounterColor;
  /**
   * The size of the badge
   * @default 'md'
   */
  size?: PhxBadgeCounterSize;
  /**
   * The border variant of the badge
   * @default 'circle'
   */
  border?: PhxBadgeCounterBorder;
}

const getColorStyles = (color: PhxBadgeCounterColor) => {
  const colorMap: Record<PhxBadgeCounterColor, { bg: string; text: string; border: string }> = {
    default: {
      bg: 'transparent',
      text: neutral[900],
      border: neutral[200],
    },
    primary: {
      bg: primary[800],
      text: neutral[25],
      border: primary[800],
    },
    gray: {
      bg: neutral[100],
      text: neutral[900],
      border: neutral[200],
    },
    red: {
      bg: error[800],
      text: neutral[25],
      border: error[800],
    },
  };

  return colorMap[color];
};

function badgeSx(
  color: PhxBadgeCounterColor,
  size: PhxBadgeCounterSize,
  border: PhxBadgeCounterBorder,
  displayNumber: number,
  hasPlus: boolean
): SxProps<Theme> {
  const colorStyles = getColorStyles(color);
  const isSm = size === 'sm';
  const multiDigit = displayNumber > 9 || hasPlus;
  const dim = isSm ? 24 : 30;
  const fontSize = isSm ? 12 : 14;
  const lineHeight = isSm ? '14px' : '18px';

  const base: SxProps<Theme> = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    backgroundColor: colorStyles.bg,
    color: colorStyles.text,
    border: `1px solid ${colorStyles.border}`,
    fontSize,
    fontWeight: 400,
    lineHeight,
  };

  if (border === 'circle' && !multiDigit) {
    return {
      ...base,
      width: dim,
      height: dim,
      minWidth: dim,
      maxWidth: dim,
      padding: 0,
      borderRadius: '50%',
    };
  }

  if (border === 'circle' && multiDigit) {
    const py = isSm ? '6px' : '7px';
    const px = isSm ? '8px' : '10px';
    return {
      ...base,
      minHeight: dim,
      padding: `${py} ${px}`,
      borderRadius: '100px',
      minWidth: 'auto',
    };
  }

  const py = isSm ? '6px' : '7px';
  const px = multiDigit ? (isSm ? '8px' : '10px') : isSm ? '12px' : '14px';
  return {
    ...base,
    height: dim,
    minWidth: multiDigit ? 'auto' : dim,
    padding: `${py} ${px}`,
    borderRadius: 1,
  };
}

const plusSx = (size: PhxBadgeCounterSize): SxProps<Theme> => ({
  fontSize: size === 'sm' ? '10px' : '12px',
  lineHeight: 1,
  verticalAlign: 'super',
  marginLeft: '1px',
});

export const PhxBadgeCounter: React.FC<PhxBadgeCounterProps> = ({
  number,
  tooltipTitle,
  tooltipProps,
  color = 'default',
  size = 'md',
  border = 'circle',
}) => {
  const shouldShowPlus = number > 99;
  const displayNumber = number <= 99 ? number : 99;

  const badgeBoxSx = useMemo(
    () => badgeSx(color, size, border, displayNumber, shouldShowPlus),
    [color, size, border, displayNumber, shouldShowPlus]
  );

  const badgeClassName = `lex-badge-counter lex-badge-counter-${color} lex-badge-counter-${size}`;

  const badge = (
    <Box
      component='span'
      className={badgeClassName}
      sx={badgeBoxSx}
    >
      <Typography
        variant='captionMd'
        sx={{ color: 'inherit', lineHeight: '14px' }}
      >
        {displayNumber}
      </Typography>
      {shouldShowPlus && <Box component='sup' sx={plusSx(size)}>+</Box>}
    </Box>
  );

  if (tooltipTitle != null) {
    return (
      <Tooltip
        title={tooltipTitle}
        {...tooltipProps}
      >
        <Box
          component='span'
          sx={{
            display: 'inline-flex',
            maxWidth: 'max-content',
            cursor: 'default',
            outline: 'none',
            '&:focus-visible': {
              boxShadow: (theme) => `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${primary[500]}`,
              borderRadius: 0.5,
            },
          }}
          tabIndex={0}
        >
          {badge}
        </Box>
      </Tooltip>
    );
  }

  return badge;
};
